import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { Button } from '@/components/ui'
import { formatDocumentTitle } from '@/lib/document-title'

// Simple icon components for demo
function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  )
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
    </svg>
  )
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  )
}

export const Route = createFileRoute('/_dev/ui-preview/')({
  component: UIPreviewPage,
})

function UIPreviewPage() {
  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle('UI Preview', 'Dev')}</title>
      </Helmet>
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-12">
          <header>
            <h1 className="text-2xl font-semibold text-text-primary">
              Hayven UI Components
            </h1>
            <p className="text-text-secondary mt-2">
              Button component variants preview
            </p>
          </header>

          {/* Primary Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Primary Button
            </h2>
            <p className="text-sm text-text-secondary">
              Dark indigo background, white text. Used for main actions like
              Save, Submit.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="primary" size="sm">
                Small
              </Button>
              <Button variant="primary">Save</Button>
              <Button variant="primary" size="lg">
                Large
              </Button>
              <Button variant="primary" disabled>
                Disabled
              </Button>
              <Button variant="primary" loading>
                Loading
              </Button>
            </div>
          </section>

          {/* Secondary Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Secondary Button
            </h2>
            <p className="text-sm text-text-secondary">
              Cyan/teal background, dark text. Used for secondary actions.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="secondary" size="sm">
                Small
              </Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="secondary" size="lg">
                Large
              </Button>
              <Button variant="secondary" disabled>
                Disabled
              </Button>
            </div>
          </section>

          {/* Outline Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Outline Button
            </h2>
            <p className="text-sm text-text-secondary">
              White background, gray border. Hover shows cyan background. Used
              for CHECK-IN, CANCEL.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="outline" size="sm">
                Small
              </Button>
              <Button variant="outline">CHECK-IN</Button>
              <Button variant="outline" shape="pill">
                CHECK-IN (Pill)
              </Button>
              <Button variant="outline" size="lg">
                CANCEL
              </Button>
              <Button variant="outline" disabled>
                Disabled
              </Button>
            </div>
            <div className="flex gap-4 mt-4">
              <Button variant="outline">Cancel</Button>
              <Button variant="primary">Save</Button>
            </div>
          </section>

          {/* Ghost Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Ghost Button
            </h2>
            <p className="text-sm text-text-secondary">
              Transparent background, hover shows cyan background. Used for Edit
              button with icon.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="ghost" size="sm" leftIcon={<PencilIcon />}>
                Edit
              </Button>
              <Button variant="ghost" leftIcon={<PencilIcon />}>
                Edit
              </Button>
              <Button variant="ghost" size="lg" leftIcon={<PencilIcon />}>
                Edit Large
              </Button>
              <Button variant="ghost" disabled leftIcon={<PencilIcon />}>
                Disabled
              </Button>
            </div>
          </section>

          {/* Action (CTA) Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Action Button (CTA)
            </h2>
            <p className="text-sm text-text-secondary">
              Pink/magenta background, white text. Used for primary CTAs like
              ADD CLIENT.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="action" size="sm" shape="pill">
                Small
              </Button>
              <Button variant="action" shape="pill">
                ADD CLIENT
              </Button>
              <Button
                variant="action"
                shape="pill"
                size="lg"
                leftIcon={<PlusIcon />}
              >
                ADD CLIENT
              </Button>
              <Button variant="action" shape="pill" disabled>
                Disabled
              </Button>
            </div>
          </section>

          {/* Dark Variant */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Dark Button
            </h2>
            <p className="text-sm text-text-secondary">
              Dark navy background, white text. Used for Messages button.
            </p>
            <div className="flex flex-wrap items-center gap-4 bg-[#0F172A] p-4 rounded-md">
              <Button variant="dark" leftIcon={<MessageIcon />}>
                Messages
              </Button>
              <Button variant="dark" size="icon">
                <MoreIcon />
              </Button>
              <Button variant="dark" disabled leftIcon={<MessageIcon />}>
                Disabled
              </Button>
            </div>
          </section>

          {/* Icon Buttons */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Icon Buttons
            </h2>
            <p className="text-sm text-text-secondary">
              Square icon-only buttons in various sizes.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button variant="primary" size="icon-sm">
                <PlusIcon />
              </Button>
              <Button variant="primary" size="icon">
                <PlusIcon />
              </Button>
              <Button variant="primary" size="icon-lg">
                <PlusIcon />
              </Button>
              <Button variant="outline" size="icon">
                <PencilIcon />
              </Button>
              <Button variant="ghost" size="icon">
                <MoreIcon />
              </Button>
              <Button variant="action" size="icon" shape="pill">
                <PlusIcon />
              </Button>
            </div>
          </section>

          {/* Full Width */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-text-primary border-b pb-2">
              Full Width Button
            </h2>
            <div className="max-w-md space-y-3">
              <Button variant="primary" fullWidth>
                Full Width Primary
              </Button>
              <Button variant="outline" fullWidth>
                Full Width Outline
              </Button>
              <Button variant="action" fullWidth shape="pill">
                Full Width Action
              </Button>
            </div>
          </section>
        </div>
      </div>
    </>
  )
}
